import React, { useContext, useState } from "react";
import {
  Avatar,
  Badge,
  IconButton,
  makeStyles,
  Tooltip,
  Typography
} from "@material-ui/core";

import { useHistory, useParams } from "react-router-dom";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import GroupOutlinedIcon from "@material-ui/icons/GroupOutlined";

import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import { getBackendUrl } from "../../config";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    background:
      theme.palette.type === "dark"
        ? "#0B1220"
        : "#FFFFFF"
  },

  chatList: {
    flex: 1,
    overflowY: "auto",
    padding: "8px 8px 12px",
    ...theme.scrollbarStyles
  },

  item: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    gap: 11,
    width: "100%",
    minHeight: 70,
    padding: "10px 10px",
    marginBottom: 3,
    border: "1px solid transparent",
    borderRadius: 14,
    cursor: "pointer",
    transition: "all .16s ease",

    "&:hover": {
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,.04)"
          : "#F8FAFC"
    },

    "&:hover $actions": {
      opacity: 1
    }
  },

  itemActive: {
    background:
      theme.palette.type === "dark"
        ? "rgba(0,87,255,.14)"
        : "#EFF6FF",
    borderColor:
      theme.palette.type === "dark"
        ? "rgba(59,130,246,.24)"
        : "#DBEAFE"
  },

  avatarWrap: {
    flexShrink: 0
  },

  avatar: {
    width: 44,
    height: 44,
    fontSize: 15,
    fontWeight: 800,
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#0057FF,#00A3FF)"
  },

  body: {
    flex: 1,
    minWidth: 0
  },

  top: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginBottom: 4
  },

  title: {
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 13.5,
    fontWeight: 800,
    color:
      theme.palette.type === "dark"
        ? "#F8FAFC"
        : "#0F172A"
  },

  time: {
    flexShrink: 0,
    fontSize: 10.5,
    color:
      theme.palette.type === "dark"
        ? "#64748B"
        : "#94A3B8"
  },

  bottom: {
    display: "flex",
    alignItems: "center",
    gap: 6
  },

  preview: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: 12,
    color:
      theme.palette.type === "dark"
        ? "#94A3B8"
        : "#64748B"
  },

  unread: {
    minWidth: 20,
    height: 20,
    padding: "0 6px",
    borderRadius: 999,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0057FF",
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: 900
  },

  actions: {
    position: "absolute",
    right: 8,
    bottom: 5,
    display: "flex",
    opacity: 0,
    transition: "opacity .15s ease",
    background:
      theme.palette.type === "dark"
        ? "#111827"
        : "#FFFFFF",
    borderRadius: 9,
    boxShadow: "0 4px 12px rgba(15,23,42,.09)"
  },

  actionButton: {
    padding: 4,
    color: "#64748B"
  },

  empty: {
    height: "100%",
    padding: "36px 20px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    color: "#94A3B8"
  }
}));

const initials = name =>
  String(name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase();

const formatTime = value => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();

  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit"
    });
  }

  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit"
  });
};

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat
}) {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { id } = useParams();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});

  const backendUrl = getBackendUrl();

  const unreadMessages = chat => {
    const currentUser = (chat?.users || []).find(
      item => Number(item.userId) === Number(user.id)
    );

    return Number(currentUser?.unreads || 0);
  };

  const getParticipants = chat =>
    (chat?.users || [])
      .map(item => item.user)
      .filter(Boolean);

  const getOtherParticipant = chat =>
    getParticipants(chat).find(
      participant => Number(participant.id) !== Number(user.id)
    ) || getParticipants(chat)[0];

  const getTitle = chat => {
    const explicitTitle = String(chat?.title || "").trim();

    if (explicitTitle) return explicitTitle;

    const other = getOtherParticipant(chat);
    return other?.name || "Conversa interna";
  };

  const getSubtitle = chat => {
    const participants = getParticipants(chat);

    if (participants.length > 2) {
      return `${participants.length} participantes`;
    }

    return chat?.lastMessage || "Nenhuma mensagem ainda";
  };

  const getAvatar = chat => {
    const participant = getOtherParticipant(chat);

    return participant?.profileImage
      ? `${backendUrl}/public/avatar/${participant.profileImage}`
      : undefined;
  };

  const goToMessages = async chat => {
    if (unreadMessages(chat) > 0) {
      try {
        await api.post(`/chats/${chat.id}/read`, {
          userId: user.id
        });
      } catch (err) {}
    }

    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
      handleSelectChat(chat);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
    setConfirmModalOpen(false);
  };

  return (
    <>
      <ConfirmationModal
        title="Excluir conversa"
        open={confirmationModal}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser revertida. Deseja continuar?
      </ConfirmationModal>

      <div className={classes.mainContainer}>
        <div className={classes.chatList}>
          {Array.isArray(chats) && chats.length > 0 ? (
            chats.map(chat => {
              const unread = unreadMessages(chat);
              const title = getTitle(chat);
              const participants = getParticipants(chat);

              return (
                <div
                  key={chat.id}
                  className={`${classes.item} ${
                    chat.uuid === id ? classes.itemActive : ""
                  }`}
                  onClick={() => goToMessages(chat)}
                  role="button"
                  tabIndex={0}
                >
                  <div className={classes.avatarWrap}>
                    <Badge
                      color="primary"
                      overlap="circle"
                      variant={unread > 0 ? "dot" : "standard"}
                    >
                      <Avatar
                        src={getAvatar(chat)}
                        className={classes.avatar}
                      >
                        {participants.length > 2 ? (
                          <GroupOutlinedIcon fontSize="small" />
                        ) : (
                          initials(title)
                        )}
                      </Avatar>
                    </Badge>
                  </div>

                  <div className={classes.body}>
                    <div className={classes.top}>
                      <Typography className={classes.title}>
                        {title}
                      </Typography>

                      <span className={classes.time}>
                        {formatTime(chat.updatedAt)}
                      </span>
                    </div>

                    <div className={classes.bottom}>
                      <span className={classes.preview}>
                        {getSubtitle(chat)}
                      </span>

                      {unread > 0 ? (
                        <span className={classes.unread}>
                          {unread > 99 ? "99+" : unread}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {Number(chat.ownerId) === Number(user.id) ? (
                    <div
                      className={classes.actions}
                      onClick={event => event.stopPropagation()}
                    >
                      <Tooltip title="Editar conversa">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          onClick={async () => {
                            await goToMessages(chat);
                            handleEditChat(chat);
                          }}
                        >
                          <EditOutlinedIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Excluir conversa">
                        <IconButton
                          size="small"
                          className={classes.actionButton}
                          onClick={() => {
                            setSelectedChat(chat);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteOutlineIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </div>
                  ) : null}
                </div>
              );
            })
          ) : (
            <div className={classes.empty}>
              <GroupOutlinedIcon
                style={{
                  fontSize: 34,
                  marginBottom: 10,
                  opacity: 0.45
                }}
              />

              <Typography style={{ fontWeight: 800, fontSize: 13 }}>
                Nenhuma conversa interna
              </Typography>

              <Typography style={{ fontSize: 11, marginTop: 4 }}>
                Crie uma conversa para falar com sua equipe.
              </Typography>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
